import {
  CalendarIcon,
  MapPinIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import {
  Avatar,
  Card,
  CardBody,
  CardFooter,
  Chip,
  Tooltip,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { MarketplaceCardResponseDto } from "../../types/types";
import { formatDate } from "../../utils/formatUtils";
import {
  formatPurseText,
  getCurrencySymbol,
  getSportColor,
} from "../../utils/opportunityAndReadyroster";
import {
  formatSportName,
  formatWeightClassName,
} from "../../utils/utilMethods";

interface MarketplaceCardProps {
  item: MarketplaceCardResponseDto;
  role?: string;
  activeTab?: "all" | "myCards";
  onView: (item: any) => void;
}

const MarketplaceCard: React.FC<MarketplaceCardProps> = ({
  item,
  onView,
}) => {
  const { t } = useTranslation("marketPlaceCards");

  const isReadyRoster = !!item?.readyRosterCard;
  const isOpportunity = !!item?.opportunityCardResponse;

  if (!isReadyRoster && !isOpportunity) {
    console.error("Invalid card data:", item);
    return null;
  }

  const opportunityCard = item.opportunityCardResponse;
  const rosterCard = item.readyRosterCard;

  const hasEvent =
    isOpportunity &&
    !!(opportunityCard?.eventId || (item as any)?.eventId);

  const dealType: string | undefined = opportunityCard?.templateJson?.dealType;
  const isSparring = dealType === "sparring";

  const details = opportunityCard?.formData?.details || {};

  const sport =
    isReadyRoster
      ? rosterCard?.sport
      : isOpportunity
      ? isSparring
        ? details?.sport || details?.discipline
        : details?.sport
      : undefined;

  const purse = isReadyRoster
    ? rosterCard?.purse
    : opportunityCard?.formData?.core?.baseAmount;

  const currency = isReadyRoster
    ? rosterCard?.currency
    : opportunityCard?.formData?.core?.currency;

  const weightClass = isReadyRoster
    ? rosterCard?.weightClass
    : isSparring
    ? undefined
    : details?.weightClass;

  const preferredRuleset = isReadyRoster
    ? rosterCard?.preferredRuleSet
    : isSparring
    ? undefined
    : details?.ruleset;

  const getLocationText = (): string | undefined => {
    if (isReadyRoster) {
      const loc = rosterCard?.location;
      if (!loc) return undefined;
      if (typeof loc === "string") return loc;
      return (
        (loc as any).country ||
        (loc as any).city ||
        (loc as any).venue
      );
    }

    if (!details) return undefined;
    if (details.location && typeof details.location === "object") {
      return (
        details.location.country ||
        details.location.city ||
        details.location.venue
      );
    }
    return details.country;
  };

  const locationText = getLocationText();

  const possibleDate = isReadyRoster
    ? rosterCard?.validUpto
    : details?.eventDate || details?.validUpto || details?.startDate || null;

  const validUpto = possibleDate;

  const durationDays = isSparring && !validUpto ? details?.durationDays : undefined;

  const isExpired = validUpto
    ? new Date(validUpto).setHours(0, 0, 0, 0) <
      new Date().setHours(0, 0, 0, 0)
    : false;

  const cardBgClass = isExpired ? "bg-red-50" : "bg-white";

  const getInitials = (firstName?: string, lastName?: string) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`;
    }
    return "OP";
  };

  const fighter = rosterCard?.fighter;
  const manager = opportunityCard?.manager;

  const getTitle = () => {
    if (fighter) {
      return `${fighter.firstName || ""} ${fighter.lastName || ""}`.trim();
    }
    if (manager) {
      return `${manager.firstName || ""} ${manager.lastName || ""}`.trim();
    }
    const oppType = opportunityCard?.formData?.opportunityType;
    if (oppType) {
      return oppType.replace(/_/g, " ").toUpperCase();
    }
    if (dealType) {
      return dealType.replace(/_/g, " ").toUpperCase();
    }
    return "Opportunity";
  };

  const getFooterDateContent = () => {
    if (isSparring && !validUpto) {
      if (durationDays) {
        return `Duration: ${durationDays} days`;
      }
      return t("event_date", { defaultValue: "Event Date" });
    }
    const label = fighter
      ? t("valid_upto")
      : t("event_date", { defaultValue: "Event Date" });
    return validUpto ? `${label}: ${formatDate(validUpto)}` : label;
  };

  return (
    <Card
      key={item.id}
      className={`${cardBgClass} shadow-sm transition-shadow border border-gray-200 w-full group relative overflow-hidden box-border flex flex-col`}
      style={{ overflowX: "hidden" }}
    >
      <CardBody
        className="p-3 cursor-pointer relative min-w-0 w-full"
        onClick={() => onView(item)}
      >
        <div className="flex items-start gap-3 min-w-0 w-full">
          <div className="flex-shrink-0">
            <Avatar
              src={fighter?.profileImage || manager?.profileImage || undefined}
              name={
                !fighter?.profileImage && fighter?.firstName && fighter?.lastName
                  ? getInitials(fighter.firstName, fighter.lastName)
                  : !manager?.profileImage &&
                    manager?.firstName &&
                    manager?.lastName
                  ? getInitials(manager.firstName, manager.lastName)
                  : getInitials()
              }
              size="sm"
              className={
                fighter ? "bg-blue-100 text-blue-600" : "bg-purple-100 text-purple-600"
              }
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2 min-w-0">
              <h3
                className="font-semibold text-gray-900 text-sm truncate min-w-0"
                title={getTitle()}
              >
                {getTitle()}
              </h3>

              {hasEvent && (
                <div
                  className="flex items-center gap-1.5 flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip content={opportunityCard?.eventName || "Event Opportunity"}>
                    <div className="flex items-center">
                      <TrophyIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 mt-2 flex-wrap min-w-0">
              {sport && (
                <Tooltip content={t("sport")}>
                  <Chip
                    color={getSportColor(sport)}
                    variant="flat"
                    size="sm"
                    className="text-xs font-medium truncate max-w-full break-words"
                  >
                    <span className="truncate block max-w-full">{formatSportName(sport)}</span>
                  </Chip>
                </Tooltip>
              )}

              {purse !== undefined && purse !== null && (
                <Tooltip content={t("purse")}>
                  <Chip
                    color="default"
                    variant="flat"
                    size="sm"
                    className="text-xs font-medium truncate max-w-full break-words"
                  >
                    <span className="truncate block max-w-full">
                      {formatPurseText(purse)} {getCurrencySymbol(currency || "")}
                    </span>
                  </Chip>
                </Tooltip>
              )}
            </div>

            <div className="flex items-center gap-2 mt-2 text-sm text-gray-600 flex-wrap min-w-0">
              {weightClass && (
                <Tooltip content={t("weight_class")}>
                  <Chip
                    color="success"
                    variant="flat"
                    size="sm"
                    className="text-xs truncate max-w-full"
                  >
                    <span className="truncate block max-w-full">{formatWeightClassName(weightClass)}</span>
                  </Chip>
                </Tooltip>
              )}

              {preferredRuleset && (
                <Tooltip content={t("preferred_ruleset")}>
                  <Chip
                    color="default"
                    variant="flat"
                    size="sm"
                    className="text-xs truncate max-w-full"
                  >
                    <span className="truncate block max-w-full">{formatSportName(preferredRuleset)}</span>
                  </Chip>
                </Tooltip>
              )}

              {locationText ? (
                <div className="flex items-center gap-1 text-sm text-gray-600 font-medium ml-1 min-w-0">
                  <MapPinIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <Tooltip content={t("location")}>
                    <span className="truncate block max-w-full">{locationText}</span>
                  </Tooltip>
                </div>
              ) : dealType ? (
                <Tooltip content="Deal Type">
                  <Chip
                    color="warning"
                    variant="flat"
                    size="sm"
                    className="text-xs truncate max-w-full"
                  >
                    <span className="truncate block max-w-full">
                      {dealType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </span>
                  </Chip>
                </Tooltip>
              ) : null}
            </div>
          </div>
        </div>
      </CardBody>

      <CardFooter className="bg-gray-50 px-4 py-3 border-t border-gray-100">
        <div className="flex items-center justify-between w-full text-xs text-gray-500 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate block min-w-0">
              {getFooterDateContent()}
            </span>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default MarketplaceCard;
